// ====================
// LA MORRA RESTAURANT
// Interactive JavaScript
// ====================

// import AOS from 'aos';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, TextPlugin, ScrollToPlugin);

// ====================
// GLOBAL VARIABLES
// ====================
let currentMenuPage = 1;
const totalMenuPages = 6;

// ====================
// INITIALIZATION
// ====================
document.addEventListener('DOMContentLoaded', function() {
    // AOS initialization disabled
    // AOS.init({
    //     duration: 800,
    //     easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    //     once: true,
    //     offset: 50,
    //     delay: 0
    // });

    // Initialize all components
    createWhatsAppDebugger(); // Debug-Helper zuerst
    initLoadingScreen();
    initNavigation();
    initHeroAnimations();
    initInfoCards();
    initMenuBook();
    initGallery();
    initScrollAnimations();
    // initWhatsAppButton(); // DEAKTIVIERT - verwende inline Lösung in HTML
    initMiscAnimations();
    initQRCodeModal();
    // initWhatsAppVisibility(); // Disabled - using simple inline solution
});

// ====================
// LOADING SCREEN
// ====================
function initLoadingScreen() {
    const loadingScreen = document.getElementById('loading-screen');
    const loadingLogo = document.querySelector('.loading-logo');
    const loadingText = document.querySelector('.loading-text');
    
    // Set initial states
    if (loadingLogo) {
        gsap.set(loadingLogo, { opacity: 0, y: 30 });
    }
    if (loadingText) {
        gsap.set(loadingText, { opacity: 0, y: 20 });
    }
    
    // Create loading timeline
    const loadingTl = gsap.timeline();
    
    if (loadingLogo) {
        loadingTl.to(loadingLogo, {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out",
            delay: 0.5
        });
    }
    
    if (loadingText) {
        loadingTl.to(loadingText, {
            opacity: 0.8,
            y: 0,
            duration: 0.8,
            ease: "power2.out"
        }, "-=0.3");
    }
    
    // Hide loading screen
    loadingTl.call(() => {
        setTimeout(() => {
            loadingScreen.classList.add('hide');
            setTimeout(() => {
                loadingScreen.style.display = 'none';
                // AOS refresh disabled
                // AOS.refresh();
            }, 800);
        }, 1500);
    });
}

// ====================
// NAVIGATION
// ====================
function initNavigation() {
    const navbar = document.getElementById('navbar');
    const hamburger = document.querySelector('.hamburger');
    const navMenu = document.querySelector('.nav-menu');
    const navLinks = document.querySelectorAll('.nav-link');

    // Navbar scroll effect
    let scrolled = false;
    
    function updateNavbar() {
        if (window.scrollY > 100 && !scrolled) {
            navbar.classList.add('scrolled');
            scrolled = true;
        } else if (window.scrollY <= 100 && scrolled) {
            navbar.classList.remove('scrolled');
            scrolled = false;
        }
    }
    
    window.addEventListener('scroll', updateNavbar);
    updateNavbar(); // Initial check

    // Mobile menu toggle
    if (hamburger && navMenu) {
        hamburger.addEventListener('click', () => {
            hamburger.classList.toggle('active');
            navMenu.classList.toggle('active');
            
            // Animate hamburger lines
            const spans = hamburger.querySelectorAll('span');
            if (hamburger.classList.contains('active')) {
                gsap.to(spans[0], { rotation: 45, y: 6, duration: 0.3 });
                gsap.to(spans[1], { opacity: 0, duration: 0.3 });
                gsap.to(spans[2], { rotation: -45, y: -6, duration: 0.3 });
            } else {
                gsap.to(spans[0], { rotation: 0, y: 0, duration: 0.3 });
                gsap.to(spans[1], { opacity: 1, duration: 0.3 });
                gsap.to(spans[2], { rotation: 0, y: 0, duration: 0.3 });
            }
        });
    }

    // Smooth scrolling for navigation links
    navLinks.forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const targetId = link.getAttribute('href');
            const targetSection = document.querySelector(targetId);
            
            if (targetSection) {
                const offsetTop = targetSection.offsetTop - 120;
                
                gsap.to(window, {
                    duration: 1.5,
                    scrollTo: { y: offsetTop, autoKill: false },
                    ease: "power3.inOut"
                });
            }

            // Close mobile menu
            if (hamburger && navMenu) {
                hamburger.classList.remove('active');
                navMenu.classList.remove('active');
                
                const spans = hamburger.querySelectorAll('span');
                gsap.to(spans[0], { rotation: 0, y: 0, duration: 0.3 });
                gsap.to(spans[1], { opacity: 1, duration: 0.3 });
                gsap.to(spans[2], { rotation: 0, y: 0, duration: 0.3 });
            }
        });
    });

    // Active navigation highlighting
    const sections = document.querySelectorAll('section[id]');
    
    const observerOptions = {
        threshold: 0.2,
        rootMargin: '-120px 0px -60% 0px'
    };

    const navObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const currentLink = document.querySelector(`a[href="#${entry.target.id}"]`);
                
                // Remove active class from all links
                navLinks.forEach(link => link.classList.remove('active'));
                
                // Add active class to current link
                if (currentLink) {
                    currentLink.classList.add('active');
                }
            }
        });
    }, observerOptions);

    sections.forEach(section => navObserver.observe(section));
}

// ====================
// HERO ANIMATIONS
// ====================
function initHeroAnimations() {
    const heroTitle = document.querySelector('.hero-title');
    const heroSubtitle = document.querySelector('.hero-subtitle');
    const heroButtons = document.querySelector('.hero-buttons');
    
    // Set initial states
    gsap.set([heroTitle, heroSubtitle, heroButtons], { opacity: 0, y: 60 });
    
    // Create hero timeline
    const heroTl = gsap.timeline({ delay: 3 }); // Wait for loading screen
    
    heroTl
        .to(heroTitle, {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "power3.out"
        })
        .to(heroSubtitle, {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out"
        }, "-=0.6")
        .to(heroButtons, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out"
        }, "-=0.4");

    // Add button hover effects
    const buttons = document.querySelectorAll('.btn');
    
    buttons.forEach(btn => {
        btn.addEventListener('mouseenter', () => {
            gsap.to(btn, {
                y: -3,
                scale: 1.02,
                duration: 0.3,
                ease: "power2.out"
            });
        });
        
        btn.addEventListener('mouseleave', () => {
            gsap.to(btn, {
                y: 0,
                scale: 1,
                duration: 0.3,
                ease: "power2.out"
            });
        });
    });

}

// ====================
// INFO CARDS
// ====================
function initInfoCards() {
    const cards = document.querySelectorAll('.info-card');
    
    cards.forEach((card, index) => {
        // Hover effect
        card.addEventListener('mouseenter', () => {
            gsap.to(card, {
                y: -15,
                scale: 1.02,
                duration: 0.4,
                ease: "power2.out"
            });
            
            const icon = card.querySelector('.card-icon');
            gsap.to(icon, {
                scale: 1.1,
                rotation: 5,
                duration: 0.4,
                ease: "power2.out"
            });
        });
        
        card.addEventListener('mouseleave', () => {
            gsap.to(card, {
                y: 0,
                scale: 1,
                duration: 0.4,
                ease: "power2.out"
            });
            
            const icon = card.querySelector('.card-icon');
            gsap.to(icon, {
                scale: 1,
                rotation: 0,
                duration: 0.4,
                ease: "power2.out"
            });
        });

        // Scroll animation
        gsap.from(card, {
            y: 50,
            opacity: 0,
            duration: 0.8,
            ease: "power3.out",
            delay: index * 0.2,
            scrollTrigger: {
                trigger: card,
                start: "top 85%",
                end: "bottom 15%",
                toggleActions: "play none none reverse"
            }
        });
    });
}

// ====================
// MENU BOOK
// ====================
function initMenuBook() {
    const prevBtn = document.getElementById('prevPage');
    const nextBtn = document.getElementById('nextPage');
    const currentPageSpan = document.querySelector('.current-page');
    const menuPages = document.querySelectorAll('.menu-page');

    function updatePageIndicator() {
        if (currentPageSpan) {
            currentPageSpan.textContent = currentMenuPage;
        }
        
        // Update button states
        if (prevBtn) {
            prevBtn.disabled = currentMenuPage === 1;
        }
        if (nextBtn) {
            nextBtn.disabled = currentMenuPage === totalMenuPages;
        }
    }

    function showPage(pageNumber) {
        // Hide all pages
        menuPages.forEach((page, index) => {
            if (index + 1 === currentMenuPage) {
                gsap.to(page, {
                    opacity: 0,
                    x: pageNumber > currentMenuPage ? -30 : 30,
                    duration: 0.3,
                    ease: "power2.inOut",
                    onComplete: () => {
                        page.classList.remove('active');
                    }
                });
            }
        });

        // Show new page
        setTimeout(() => {
            currentMenuPage = pageNumber;
            const newPage = menuPages[currentMenuPage - 1];
            
            if (newPage) {
                newPage.classList.add('active');
                gsap.fromTo(newPage, 
                    {
                        opacity: 0,
                        x: pageNumber > (currentMenuPage - 1) ? 30 : -30
                    },
                    {
                        opacity: 1,
                        x: 0,
                        duration: 0.4,
                        ease: "power2.out"
                    }
                );

                // Animate menu items
                const menuItems = newPage.querySelectorAll('.menu-item');
                gsap.from(menuItems, {
                    y: 20,
                    opacity: 0,
                    duration: 0.5,
                    stagger: 0.1,
                    ease: "power3.out",
                    delay: 0.2
                });
            }
            
            updatePageIndicator();
        }, 300);
    }

    // Navigation event listeners
    if (prevBtn) {
        prevBtn.addEventListener('click', () => {
            if (currentMenuPage > 1) {
                showPage(currentMenuPage - 1);
            }
        });
    }

    if (nextBtn) {
        nextBtn.addEventListener('click', () => {
            if (currentMenuPage < totalMenuPages) {
                showPage(currentMenuPage + 1);
            }
        });
    }

    // Initialize
    updatePageIndicator();

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        const menuSection = document.querySelector('.menu');
        const rect = menuSection.getBoundingClientRect();
        const isMenuVisible = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isMenuVisible) {
            if (e.key === 'ArrowLeft' && currentMenuPage > 1) {
                showPage(currentMenuPage - 1);
            } else if (e.key === 'ArrowRight' && currentMenuPage < totalMenuPages) {
                showPage(currentMenuPage + 1);
            }
        }
    });

    // Menu book - FORCE VISIBILITY WITHOUT ANIMATION
    gsap.set('.menu-book', { opacity: 1, visibility: 'visible', y: 0, clearProps: "all" });
    gsap.set('.menu-book *', { opacity: 1, visibility: 'visible', clearProps: "all" });
    
    // Remove any ScrollTrigger for menu-book to prevent conflicts
    console.log('✅ Menu-book animations disabled for stability');
}

// ====================
// GALLERY
// ====================
function initGallery() {
    const galleryItems = document.querySelectorAll('.gallery-item');
    
    galleryItems.forEach((item, index) => {
        // Set initial state - images start large (scale 1.3)
        const image = item.querySelector('.gallery-image');
        
        // AOS disabled - remove data attributes
        item.removeAttribute('data-aos');
        item.removeAttribute('data-aos-delay');
        
        gsap.set(image, {
            scale: 1.3
        });
        
        // Hover effects - images start large and get slightly bigger on hover
        item.addEventListener('mouseenter', () => {
            const image = item.querySelector('.gallery-image');
            const overlay = item.querySelector('.gallery-overlay');
            
            gsap.to(item, {
                y: -8,
                scale: 1.02,
                duration: 0.4,
                ease: "power2.out"
            });
            
            gsap.to(image, {
                scale: 1.35,
                duration: 0.6,
                ease: "power2.out"
            });
            
            gsap.to(overlay, {
                opacity: 1,
                duration: 0.3,
                ease: "power2.out"
            });
        });
        
        item.addEventListener('mouseleave', () => {
            const image = item.querySelector('.gallery-image');
            const overlay = item.querySelector('.gallery-overlay');
            
            gsap.to(item, {
                y: 0,
                scale: 1,
                duration: 0.4,
                ease: "power2.out"
            });
            
            gsap.to(image, {
                scale: 1.3,
                duration: 0.6,
                ease: "power2.out"
            });
            
            gsap.to(overlay, {
                opacity: 0,
                duration: 0.3,
                ease: "power2.out"
            });
        });

        // Click to expand functionality
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            console.log('Gallery item clicked:', item);
            createImageModal(item);
        });

        // Scroll animation
        gsap.from(item, {
            y: 40,
            opacity: 0,
            duration: 0.8,
            ease: "power3.out",
            delay: (index % 3) * 0.1,
            scrollTrigger: {
                trigger: item,
                start: "top 90%",
                end: "bottom 10%",
                toggleActions: "play none none reverse"
            }
        });
    });
}

function createImageModal(galleryItem) {
    const image = galleryItem.querySelector('.gallery-image');
    const title = galleryItem.querySelector('.gallery-overlay h3').textContent;
    
    // Get image class directly  
    const imageClasses = image.className.split(' ');
    const imageClass = imageClasses.find(cls => cls.startsWith('food-') || cls.startsWith('restaurant-'));
    
    console.log('Image class found:', imageClass);
    console.log('Image classes:', imageClasses);
    
    // Direct URL mapping - using higher resolution for lightbox
    const imageUrls = {
        'food-1': 'https://images.unsplash.com/photo-1589187151053-5ec8818e661b?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
        'food-2': 'https://images.unsplash.com/photo-1591972774172-084d6be1b72c?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', 
        'food-3': 'https://images.unsplash.com/photo-1614874044863-95c6cc6b6cf3?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
        'food-4': '/src/assets/images/tiramisu.jpg',
        'restaurant-1': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80',
        'restaurant-2': 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80'
    };
    
    const imageUrl = imageUrls[imageClass];
    console.log('Image URL:', imageUrl);
    
    if (!imageUrl) {
        console.error('No image URL found for class:', imageClass);
        return;
    }
    
    // Create modal with direct URL - SECURE VERSION
    const modal = document.createElement('div');
    modal.className = 'image-modal';
    
    // Create modal structure safely
    const modalBackdrop = document.createElement('div');
    modalBackdrop.className = 'modal-backdrop';
    
    const modalContent = document.createElement('div');
    modalContent.className = 'modal-content';
    
    const modalClose = document.createElement('button');
    modalClose.className = 'modal-close';
    modalClose.innerHTML = '&times;'; // Safe for HTML entity
    
    const modalImage = document.createElement('div');
    modalImage.className = 'modal-image';
    modalImage.style.backgroundImage = `url('${CSS.escape(imageUrl)}')`; // CSS.escape for safety
    
    const modalTitle = document.createElement('h3');
    modalTitle.textContent = title; // Safe: using textContent
    
    // Assemble modal
    modalContent.appendChild(modalClose);
    modalContent.appendChild(modalImage);
    modalContent.appendChild(modalTitle);
    modal.appendChild(modalBackdrop);
    modal.appendChild(modalContent);
    
    // Add modal styles
    const modalStyles = `
        .image-modal {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .modal-backdrop {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(26, 26, 26, 0.9);
            backdrop-filter: blur(10px);
        }
        
        .modal-content {
            position: relative;
            max-width: 90vw;
            max-height: 90vh;
            text-align: center;
            z-index: 10001;
        }
        
        .modal-image {
            width: 80vw;
            max-width: 800px;
            height: 60vh;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            margin-bottom: 2rem;
            border-radius: 10px;
        }
        
        .modal-close {
            position: absolute;
            top: -50px;
            right: 0;
            background: transparent;
            border: none;
            color: white;
            font-size: 2rem;
            cursor: pointer;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.3s ease;
        }
        
        .modal-close:hover {
            background: rgba(255, 255, 255, 0.1);
        }
        
        .modal-content h3 {
            color: white;
            font-size: 1.5rem;
            margin: 0;
        }
        
        @media (max-width: 768px) {
            .modal-image {
                width: 95vw;
                height: 50vh;
            }
            
            .modal-close {
                top: -40px;
                right: 10px;
            }
        }
    `;
    
    // Add styles if not already added
    if (!document.getElementById('modal-styles')) {
        const styleSheet = document.createElement('style');
        styleSheet.id = 'modal-styles';
        styleSheet.textContent = modalStyles;
        document.head.appendChild(styleSheet);
    }
    
    document.body.appendChild(modal);
    
    // Animate modal in
    gsap.set(modal, { opacity: 0 });
    gsap.set(modal.querySelector('.modal-content'), { scale: 0.8, y: 50 });
    
    gsap.to(modal, { opacity: 1, duration: 0.3 });
    gsap.to(modal.querySelector('.modal-content'), {
        scale: 1,
        y: 0,
        duration: 0.5,
        ease: "power3.out"
    });
    
    // Close modal function
    const closeModal = () => {
        gsap.to(modal, {
            opacity: 0,
            duration: 0.3,
            onComplete: () => modal.remove()
        });
    };
    
    // Event listeners
    modal.querySelector('.modal-close').addEventListener('click', closeModal);
    modal.querySelector('.modal-backdrop').addEventListener('click', closeModal);
    
    // Keyboard close
    const handleKeydown = (e) => {
        if (e.key === 'Escape') {
            closeModal();
            document.removeEventListener('keydown', handleKeydown);
        }
    };
    document.addEventListener('keydown', handleKeydown);
}

// ====================
// SCROLL ANIMATIONS
// ====================
function initScrollAnimations() {
    // Section titles
    const sectionTitles = document.querySelectorAll('.section-title');
    
    sectionTitles.forEach(title => {
        gsap.from(title, {
            y: 40,
            opacity: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
                trigger: title,
                start: "top 85%",
                end: "bottom 15%",
                toggleActions: "play none none reverse"
            }
        });
    });

    // Decorative lines
    const decorativeLines = document.querySelectorAll('.decorative-line');
    
    decorativeLines.forEach(line => {
        gsap.from(line, {
            width: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
                trigger: line,
                start: "top 85%",
                end: "bottom 15%",
                toggleActions: "play none none reverse"
            }
        });
    });


    // Contact details animation
    const contactDetails = document.querySelectorAll('.detail-item');
    
    contactDetails.forEach((detail, index) => {
        gsap.from(detail, {
            x: -30,
            opacity: 0,
            duration: 0.6,
            ease: "power3.out",
            delay: index * 0.1,
            scrollTrigger: {
                trigger: detail,
                start: "top 85%",
                end: "bottom 15%",
                toggleActions: "play none none reverse"
            }
        });
    });

    // Parallax for about and contact sections
    gsap.to('.about', {
        backgroundPosition: "50% 80%",
        ease: "none",
        scrollTrigger: {
            trigger: ".about",
            start: "top bottom",
            end: "bottom top",
            scrub: true
        }
    });
}

// ====================
// DEBUG HELPER FOR WHATSAPP BUTTON
// ====================
function createWhatsAppDebugger() {
    // Nur im Development Mode
    if (window.location.hostname === 'localhost') {
        window.debugWhatsApp = function() {
            const whatsappBtn = document.querySelector('.whatsapp-simple-btn');
            const navMenu = document.querySelector('.nav-menu');
            
            if (!whatsappBtn) {
                console.log('❌ WhatsApp Button nicht gefunden!');
                return;
            }
            
            const isMenuActive = navMenu && navMenu.classList.contains('active');
            const computedStyle = window.getComputedStyle(whatsappBtn);
            const scrollY = window.scrollY;
            
            console.log('🔍 WHATSAPP BUTTON DEBUG:', {
                menuActive: isMenuActive,
                scrollPosition: scrollY,
                buttonDisplay: computedStyle.display,
                buttonVisibility: computedStyle.visibility,
                buttonOpacity: computedStyle.opacity,
                buttonZIndex: computedStyle.zIndex,
                buttonClasses: whatsappBtn.className,
                buttonDataMenuOpen: whatsappBtn.getAttribute('data-menu-open'),
                navMenuClasses: navMenu ? navMenu.className : 'nicht gefunden'
            });
        };
        
        console.log('🔧 Debug-Funktion verfügbar: debugWhatsApp()');
    }
}

// ====================
// WHATSAPP BUTTON
// ====================
function initWhatsAppButton() {
    const whatsappBtn = document.querySelector('.whatsapp-simple-btn');
    const hamburger = document.querySelector('.hamburger');
    const navMenu = document.querySelector('.nav-menu');
    
    if (whatsappBtn) {
        // Initial Setup - Original Design bewahren
        console.log('🎯 WhatsApp Button gefunden, Setup wird initialisiert');
        
        // WICHTIG: Stelle sicher, dass das Original-Design erhalten bleibt
        // Speichere original Styles bevor GSAP sie überschreibt
        const originalStyles = {
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            background: "url('https://plus.unsplash.com/premium_photo-1733259709671-9dbf22bf02cc?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D') center/130% no-repeat",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
            zIndex: '10001',
            boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
            transition: 'all 0.8s ease'
        };
        
        // KRITISCH: GSAP darf NICHT die Position beeinflussen - nur Sichtbarkeit!
        // Alle Positions-Properties über CSS setzen, GSAP nur für opacity/scale
        whatsappBtn.style.position = 'fixed';
        whatsappBtn.style.bottom = '30px';
        whatsappBtn.style.right = '30px';
        whatsappBtn.style.left = 'auto';
        whatsappBtn.style.top = 'auto';
        
        // GSAP nur für Animation-Properties, NIEMALS für Position
        gsap.set(whatsappBtn, { 
            scale: 0.8, 
            opacity: 0,
            // Explizit KEINE Positions-Properties für GSAP
            force3D: false, // Verhindert Hardware-Beschleunigung-Verschiebungen
            transformOrigin: "center center" // Zentrale Transformation
        });
        
        // NEUE updateWhatsAppVisibility Funktion mit verbesserter Logic
        function updateWhatsAppVisibility() {
            const isMenuOpen = navMenu && navMenu.classList.contains('active');
            const isScrollActive = ScrollTrigger.getById('whatsapp-scroll-trigger')?.isActive;
            
            console.log('🔄 Update WhatsApp Visibility:', { isMenuOpen, isScrollActive });
            
            if (isMenuOpen) {
                // PRIORITÄT 1: Menü ist offen - Button MUSS versteckt werden
                console.log('🔴 FORCE HIDE - Menü ist offen');
                forceHideWhatsAppButton();
            } else if (isScrollActive) {
                // PRIORITÄT 2: Menü ist geschlossen UND Scroll-Position erlaubt Anzeige
                console.log('🟢 SHOW - Menü geschlossen, Scroll aktiv');
                whatsappBtn.classList.remove('menu-open');
                whatsappBtn.removeAttribute('data-menu-open');
                showWhatsAppButton();
            } else {
                // PRIORITÄT 3: Menü geschlossen, aber Scroll-Position erfordert Verstecken
                console.log('⚪ HIDE - Menü geschlossen, Scroll inaktiv');
                whatsappBtn.classList.remove('menu-open');
                whatsappBtn.removeAttribute('data-menu-open');
                hideWhatsAppButton();
            }
        }
        
        // ÜBERARBEITETER ScrollTrigger mit Menü-Status-Integration
        ScrollTrigger.create({
            id: 'whatsapp-scroll-trigger',
            trigger: ".info-cards",
            start: "top bottom",
            end: 99999,
            onToggle: self => {
                console.log('📜 ScrollTrigger Toggle:', { isActive: self.isActive });
                
                // KRITISCH: Erst Menü-Status prüfen, dann scroll-basierte Anzeige
                const isMenuOpen = navMenu && navMenu.classList.contains('active');
                
                if (isMenuOpen) {
                    // Menü ist offen - Button MUSS versteckt bleiben, egal was ScrollTrigger sagt
                    console.log('🔴 Menü ist offen - Button bleibt versteckt (ScrollTrigger ignoriert)');
                    forceHideWhatsAppButton();
                    return; // WICHTIG: Früher Return um ScrollTrigger-Logic zu umgehen
                }
                
                // Menü ist geschlossen - normale ScrollTrigger-Logic
                if (self.isActive) {
                    console.log('🟢 ScrollTrigger: Button einblenden (Menü ist geschlossen)');
                    whatsappBtn.classList.add('visible');
                    showWhatsAppButton();
                } else {
                    console.log('⚪ ScrollTrigger: Button ausblenden (außerhalb Scroll-Bereich)');
                    whatsappBtn.classList.remove('visible');
                    hideWhatsAppButton();
                }
            },
            onRefresh: () => {
                // Bei ScrollTrigger Refresh auch Menü-Status berücksichtigen
                setTimeout(updateWhatsAppVisibility, 10);
            }
        });
        
        // Separate Funktionen für saubere Button-Kontrolle
        function showWhatsAppButton() {
            // STEP 1: CSS für Position und Design (NICHT GSAP!)
            whatsappBtn.style.cssText = `
                position: fixed !important;
                bottom: 30px !important;
                right: 30px !important;
                top: auto !important;
                left: auto !important;
                width: 70px !important;
                height: 70px !important;
                border-radius: 50% !important;
                background: url('https://plus.unsplash.com/premium_photo-1733259709671-9dbf22bf02cc?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D') center/130% no-repeat !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                text-decoration: none !important;
                z-index: 10001 !important;
                box-shadow: 0 8px 25px rgba(0,0,0,0.3) !important;
                visibility: visible !important;
                pointer-events: auto !important;
                transition: opacity 0.5s ease, transform 0.5s ease !important;
            `;
            
            // STEP 2: Nur GSAP für sanfte Einblendung (KEINE Position!)
            gsap.killTweensOf(whatsappBtn); // Stoppe alle anderen Animationen
            gsap.fromTo(whatsappBtn, 
                { 
                    scale: 0.8, 
                    opacity: 0,
                    force3D: false // Wichtig: Verhindert 3D-Transform-Verschiebungen
                }, 
                { 
                    scale: 1, 
                    opacity: 1, 
                    duration: 0.5, 
                    ease: "back.out(1.7)",
                    force3D: false // Auch im Target: keine 3D-Transforms
                }
            );
        }
        
        function hideWhatsAppButton() {
            // Stoppe alle laufenden Animationen um Konflikte zu vermeiden
            gsap.killTweensOf(whatsappBtn);
            
            gsap.to(whatsappBtn, {
                scale: 0.8,
                opacity: 0,
                duration: 0.3,
                ease: "power2.in",
                force3D: false, // Keine 3D-Transforms während Animation
                onComplete: () => {
                    whatsappBtn.style.display = 'none';
                    whatsappBtn.style.visibility = 'hidden';
                    // Position bleibt fixed - nie ändern!
                    whatsappBtn.style.position = 'fixed';
                    whatsappBtn.style.bottom = '30px';
                    whatsappBtn.style.right = '30px';
                }
            });
        }
        
        function forceHideWhatsAppButton() {
            // Stoppe alle GSAP Animationen auf diesem Element SOFORT
            gsap.killTweensOf(whatsappBtn);
            
            // FALLBACK LEVEL 1: CSS + Inline Styles mit fester Position
            whatsappBtn.classList.add('menu-open');
            whatsappBtn.setAttribute('data-menu-open', 'true');
            whatsappBtn.style.cssText = `
                position: fixed !important;
                bottom: 30px !important;
                right: 30px !important;
                top: auto !important;
                left: auto !important;
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                pointer-events: none !important;
                z-index: -9999 !important;
                transform: scale(0) !important;
                width: 0 !important;
                height: 0 !important;
                overflow: hidden !important;
            `;
            
            // FALLBACK LEVEL 2: DOM Manipulation (Nuclear Option)
            // Entferne den Button temporär aus dem DOM für 100ms, dann wieder einfügen (versteckt)
            if (whatsappBtn.parentElement) {
                const parent = whatsappBtn.parentElement;
                const nextSibling = whatsappBtn.nextSibling;
                
                // Button aus DOM entfernen
                parent.removeChild(whatsappBtn);
                console.log('💣 DOM FALLBACK: Button temporär aus DOM entfernt');
                
                // Nach 100ms wieder einfügen (versteckt)
                setTimeout(() => {
                    if (nextSibling) {
                        parent.insertBefore(whatsappBtn, nextSibling);
                    } else {
                        parent.appendChild(whatsappBtn);
                    }
                    
                    // Stelle sicher, dass er versteckt bleibt
                    whatsappBtn.style.display = 'none';
                    whatsappBtn.style.visibility = 'hidden';
                    whatsappBtn.style.opacity = '0';
                    console.log('🔄 DOM FALLBACK: Button wieder eingefügt (versteckt)');
                }, 100);
            }
        }
        
        // ZUSÄTZLICHE FALLBACK-FUNKTION: Globale Override-Überwachung
        function createGlobalMenuWatcher() {
            // Überwache Body-Klassen
            const bodyObserver = new MutationObserver(() => {
                const isMenuOpen = navMenu && navMenu.classList.contains('active');
                if (isMenuOpen && whatsappBtn) {
                    // Notfall-Verstecken wenn alle anderen Methoden versagen
                    whatsappBtn.style.setProperty('display', 'none', 'important');
                    whatsappBtn.style.setProperty('opacity', '0', 'important');
                    whatsappBtn.style.setProperty('visibility', 'hidden', 'important');
                    whatsappBtn.setAttribute('data-force-hidden', 'true');
                }
            });
            
            bodyObserver.observe(document.body, {
                attributes: true,
                subtree: true,
                attributeFilter: ['class']
            });
            
            // Intervall-basierte Backup-Überwachung
            let backupCheckInterval = setInterval(() => {
                const isMenuOpen = navMenu && navMenu.classList.contains('active');
                const buttonVisible = whatsappBtn && 
                    window.getComputedStyle(whatsappBtn).display !== 'none' && 
                    window.getComputedStyle(whatsappBtn).visibility !== 'hidden';
                
                if (isMenuOpen && buttonVisible) {
                    console.log('🚨 BACKUP DETECTION: Button war sichtbar obwohl Menü offen - Force Hide');
                    forceHideWhatsAppButton();
                }
            }, 200);
            
            // Cleanup nach 30 Sekunden um Performance zu schonen
            setTimeout(() => {
                clearInterval(backupCheckInterval);
                bodyObserver.disconnect();
                console.log('🧹 Backup-Überwachung nach 30s beendet');
            }, 30000);
        }

        // Watch for menu toggle changes with multiple methods
        if (hamburger && navMenu) {
            // Method 1: MutationObserver
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        console.log('🔄 Menu class changed, updating WhatsApp visibility');
                        setTimeout(updateWhatsAppVisibility, 10);
                    }
                });
            });
            
            observer.observe(navMenu, {
                attributes: true,
                attributeFilter: ['class']
            });
            
            // Method 2: Direct click listeners on hamburger
            hamburger.addEventListener('click', () => {
                console.log('🍔 Hamburger clicked');
                setTimeout(updateWhatsAppVisibility, 50);
                setTimeout(updateWhatsAppVisibility, 200);
                setTimeout(updateWhatsAppVisibility, 500);
            });
            
            // Method 3: Periodic check while menu might be changing
            let menuCheckInterval;
            const startMenuCheck = () => {
                clearInterval(menuCheckInterval);
                menuCheckInterval = setInterval(() => {
                    updateWhatsAppVisibility();
                }, 100);
                setTimeout(() => clearInterval(menuCheckInterval), 2000);
            };
            
            hamburger.addEventListener('click', startMenuCheck);
            
            // Aktiviere globale Backup-Überwachung
            createGlobalMenuWatcher();
        }

        // Hover effect (only if button is visible)
        whatsappBtn.addEventListener('mouseenter', () => {
            if (whatsappBtn.style.visibility !== 'hidden' && whatsappBtn.style.display !== 'none') {
                gsap.to(whatsappBtn, {
                    scale: 1.1,
                    duration: 0.3,
                    ease: "power2.out"
                });
            }
        });
        
        whatsappBtn.addEventListener('mouseleave', () => {
            if (whatsappBtn.style.visibility !== 'hidden' && whatsappBtn.style.display !== 'none') {
                gsap.to(whatsappBtn, {
                    scale: 1,
                    duration: 0.3,
                    ease: "power2.out"
                });
            }
        });

        // Click animation
        whatsappBtn.addEventListener('click', () => {
            if (whatsappBtn.style.visibility !== 'hidden' && whatsappBtn.style.display !== 'none') {
                gsap.to(whatsappBtn, {
                    scale: 0.9,
                    duration: 0.1,
                    yoyo: true,
                    repeat: 1,
                    ease: "power2.inOut"
                });
            }
        });
    }
}

// ====================
// MISCELLANEOUS ANIMATIONS
// ====================
function initMiscAnimations() {
    // Smooth page transitions
    gsap.from("body", {
        opacity: 0,
        duration: 0.5,
        ease: "power2.out",
        delay: 0.2
    });

    // Page transition animation
    gsap.from("body", {
        opacity: 1,
        duration: 0.5,
        ease: "power2.out"
    });
}

// ====================
// UTILITY FUNCTIONS
// ====================

// Throttle function for scroll events
function throttle(func, limit) {
    let inThrottle;
    return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    }
}

// Debounce function for resize events
function debounce(func, wait, immediate) {
    let timeout;
    return function() {
        const context = this;
        const args = arguments;
        const later = function() {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

// ====================
// EVENT LISTENERS
// ====================

// Handle resize events
window.addEventListener('resize', debounce(() => {
    ScrollTrigger.refresh();
    // AOS.refresh(); // Disabled
}, 300));

// Performance optimization
window.addEventListener('scroll', throttle(() => {
    // Custom scroll effects can go here
}, 16));

// ====================
// ACCESSIBILITY
// ====================

// Focus management for modal
document.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
        const modal = document.querySelector('.image-modal');
        if (modal) {
            const focusableElements = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey && document.activeElement === firstElement) {
                lastElement.focus();
                e.preventDefault();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
                firstElement.focus();
                e.preventDefault();
            }
        }
    }
});

// Reduced motion support
if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    gsap.globalTimeline.timeScale(0);
    // AOS.init({ disable: true }); // Disabled
}

// ====================
// QR CODE MODAL
// ====================
function initQRCodeModal() {
    const qrBtn = document.getElementById('qrCodeBtn');
    const modal = document.getElementById('qrModal');
    const closeBtn = modal?.querySelector('.qr-close');
    const qrContainer = document.getElementById('qrcode');

    if (!qrBtn || !modal) return;

    // Generate vCard data
    function generateVCard() {
        return `BEGIN:VCARD
VERSION:3.0
FN:Ristorante LA MORRA
ORG:Ristorante LA MORRA
ADR:;;Hauptplatz 11-14/1/Top 14, EKZ Rosenarcade;Tulln;Niederösterreich;3430;Österreich
TEL:+43227282344
EMAIL:info@lamorra-tulln.at
URL:https://lamorra-tulln.at
CATEGORIES:Restaurant,Italienisch,Gastronomie
NOTE:Authentische italienische Küche seit Jahren in Tulln. Öffnungszeiten: Mo-Sa 9:00-22:00, So geschlossen.
END:VCARD`;
    }

    // Generate QR Code using a simple library alternative
    function generateQRCode(text) {
        // Create a simple QR code using an online API (fallback method)
        const size = 200;
        const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}`;
        
        const img = document.createElement('img');
        img.src = qrApiUrl;
        img.alt = 'QR Code für Restaurant Kontakt';
        img.style.width = `${size}px`;
        img.style.height = `${size}px`;
        
        return img;
    }

    // Download vCard function
    function downloadVCard() {
        const vcard = generateVCard();
        const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        
        // Create a temporary download link
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Ristorante_LA_MORRA.vcf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        URL.revokeObjectURL(url);
    }

    // Show modal
    qrBtn.addEventListener('click', () => {
        const vcard = generateVCard();
        const qrCode = generateQRCode(vcard);
        
        // Clear previous QR code
        qrContainer.innerHTML = '';
        qrContainer.appendChild(qrCode);
        
        modal.style.display = 'block';
        document.body.style.overflow = 'hidden';
    });

    // Add vCard download button functionality
    const vcardDownloadBtn = document.getElementById('vcardDownloadBtn');
    if (vcardDownloadBtn) {
        vcardDownloadBtn.addEventListener('click', downloadVCard);
    }

    // Close modal
    function closeModal() {
        modal.style.display = 'none';
        document.body.style.overflow = 'auto';
    }

    closeBtn?.addEventListener('click', closeModal);
    
    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeModal();
        }
    });
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.style.display === 'block') {
            closeModal();
        }
    });
}

// ====================
// WHATSAPP BUTTON VISIBILITY
// ====================
function initWhatsAppVisibility() {
    const whatsAppBtn = document.querySelector('.whatsapp-simple-btn');
    const heroSection = document.getElementById('home');
    const infoCardsSection = document.querySelector('.info-cards');
    
    if (!whatsAppBtn || !heroSection || !infoCardsSection) return;
    
    console.log('🔧 WhatsApp Button Initialization');
    
    let isLoadingComplete = false;
    
    // Wait for loading to complete (4 seconds after page load)
    setTimeout(() => {
        isLoadingComplete = true;
        console.log('✅ Loading complete, button can now be shown');
    }, 4000);
    
    // Simple logic: Show when info cards are visible AND loading is complete
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.target === infoCardsSection) {
                // Info cards section
                if (entry.isIntersecting && isLoadingComplete) {
                    console.log('🟢 Showing WhatsApp button');
                    whatsAppBtn.classList.add('show');
                    
                    // Animate in
                    gsap.to(whatsAppBtn, {
                        scale: 1,
                        duration: 0.5,
                        ease: "back.out(1.7)"
                    });
                }
            } else if (entry.target === heroSection) {
                // Hero section
                if (entry.isIntersecting) {
                    console.log('🔴 Hiding WhatsApp button (hero visible)');
                    whatsAppBtn.classList.remove('show');
                    
                    // Animate out
                    gsap.to(whatsAppBtn, {
                        scale: 0.8,
                        duration: 0.3,
                        ease: "power2.in"
                    });
                }
            }
        });
    }, {
        threshold: 0.3,
        rootMargin: '-50px'
    });
    
    // Observe both sections
    observer.observe(infoCardsSection);
    observer.observe(heroSection);
}

// ====================
// EXPORT FOR DEBUGGING
// ====================
window.LAMorraRestaurant = {
    currentMenuPage,
    totalMenuPages,
    initLoadingScreen,
    initNavigation,
    initHeroAnimations,
    initInfoCards,
    initMenuBook,
    initGallery,
    initScrollAnimations,
    initWhatsAppButton,
    initMiscAnimations,
    initQRCodeModal,
    initWhatsAppVisibility
};

console.log('🍕 LA MORRA Restaurant website loaded successfully!');